
import { pb } from '@/lib/pocketbase';

export const updateOnlineStatus = async (userId: string, userRole: string, isOnline: boolean = true) => {
  const callTime = new Date().toISOString();
  try {
    console.log(`🔄 [ONLINE STATUS ${callTime}] Starting update for user ${userId} with role ${userRole} to ${isOnline}`);
    console.log(`🔄 [ONLINE STATUS] User ID type: ${typeof userId}, value:`, userId);
    console.log(`🔄 [ONLINE STATUS] Call stack:`, new Error().stack);
    
    // Update tutor online status if user is a tutor
    if (userRole === 'tutor') {
      try {
        console.log(`🔍 [TUTOR LOOKUP] Searching for tutor profile with user ID: ${userId}`);
        
        // First, find the tutor profile by user ID
        const tutorProfile = await pb.collection('tutors').getFirstListItem(`user = "${userId}"`);
        
        console.log(`📋 [TUTOR FOUND] Profile details:`, {
          id: tutorProfile.id,
          name: tutorProfile.name,
          user: tutorProfile.user,
          fullRecord: tutorProfile
        });
        
        if (!tutorProfile.id || tutorProfile.id.trim() === '') {
          console.error('❌ [ERROR] Tutor profile has invalid ID:', tutorProfile.id);
          return;
        }
        
        // Update or create the tutor's online status in the new collection
        try {
          console.log(`🔍 [STATUS LOOKUP] Searching for existing status with tutorId: ${tutorProfile.id}`);
          const statusRecord = await pb.collection('tutor_online_status').getFirstListItem(`tutorId = "${tutorProfile.id}"`);
          console.log(`📋 [STATUS FOUND] Existing status:`, {
            id: statusRecord.id,
            tutorId: statusRecord.tutorId,
            currentStatus: statusRecord.isOnline
          });
          
          // Update existing status record
          const updateData = {
            tutorId: tutorProfile.id,
            isOnline: isOnline,
            lastSeen: new Date().toISOString()
          };
          console.log(`📝 [UPDATE] Updating with data:`, updateData);
          
          const updatedRecord = await pb.collection('tutor_online_status').update(statusRecord.id, updateData);
          
          console.log(`✅ [SUCCESS] Status updated:`, {
            recordId: updatedRecord.id,
            tutorId: updatedRecord.tutorId,
            newStatus: updatedRecord.isOnline,
            lastSeen: updatedRecord.lastSeen
          });
        } catch (statusError: any) {
          if (statusError?.status === 404) {
            console.log(`❌ [NOT FOUND] No status record exists, creating new one`);
            
            const createData = {
              tutorId: tutorProfile.id,
              isOnline: isOnline,
              lastSeen: new Date().toISOString()
            };
            console.log(`📝 [CREATE] Creating with data:`, createData);
            
            // Create new status record if it doesn't exist
            const newRecord = await pb.collection('tutor_online_status').create(createData);
            
            console.log(`✅ [SUCCESS] Status created:`, {
              recordId: newRecord.id,
              tutorId: newRecord.tutorId,
              status: newRecord.isOnline,
              lastSeen: newRecord.lastSeen,
              fullRecord: newRecord
            });
          } else {
            console.error('💥 [ERROR] Status operation failed:', {
              status: statusError?.status,
              message: statusError?.message,
              data: statusError?.data,
              fullError: statusError
            });
            throw statusError;
          }
        }
        
      } catch (error: any) {
        if (error?.status === 404) {
          console.log('❌ [NO TUTOR] No tutor profile found for user:', userId);
        } else {
          console.error('💥 [ERROR] Tutor lookup/update failed:', {
            status: error?.status,
            message: error?.message,
            data: error?.data,
            fullError: error
          });
        }
      }
    } else {
      console.log(`ℹ️ [SKIP] User role is ${userRole}, not tutor - skipping tutor status update`);
    }

    // Update live chat agent status if user is a tutor or admin
    if (userRole === 'tutor' || userRole === 'admin') {
      try {
        // First, find the live chat agent by user ID
        const agent = await pb.collection('liveChatAgents').getFirstListItem(`user = "${userId}"`);
        
        // Update the agent's online status
        const agentStatus = isOnline ? 'online' : 'offline';
        await pb.collection('liveChatAgents').update(agent.id, {
          status: agentStatus
        });
        
        console.log(`✅ Live chat agent status updated: ${agentStatus}`);
      } catch (error: any) {
        if (error?.status === 404) {
          console.log('No live chat agent profile found');
        } else {
          console.error('Error updating live chat agent status:', error);
        }
      }
    }
  } catch (error) {
    console.error('Error in updateOnlineStatus:', error);
  }
};

// Function to set user offline (for logout or page unload)
export const setUserOffline = async (userId: string, userRole: string) => {
  await updateOnlineStatus(userId, userRole, false);
};

// Function to set user online (for login)
export const setUserOnline = async (userId: string, userRole: string) => {
  await updateOnlineStatus(userId, userRole, true);
};
